'use client';

/**
 * The dashed drop surface for `FileDropZone` (LF-4642) — banner / compact /
 * mini chrome + the opt-in violet sell. A single `role="button"` region owns
 * both click-to-browse and keyboard activation; drag handlers come from the
 * caller. The visible "Select files" affordance is decorative (the region is
 * the control) to avoid nested interactive elements.
 *
 * Drag feedback is an overlay, and the real content stays mounted (just
 * `invisible`) during a drag — a drop target that changes size mid-drag
 * flickers dragenter/leave and drops unreliably, so the box dimensions are
 * held constant.
 *
 * Banner contraction (LF-4771): once files are present the banner switches out
 * its feature content — the hero tile, the AI pitch, and the benefit chips all
 * collapse away, leaving a short "add more" header. The accepted-file-types
 * line is kept (a deliberate divergence from the mock). The collapse is
 * animated via the grid-rows 0fr↔1fr technique (smooth, no text distortion,
 * reduced-motion safe) rather than jumping to the new height.
 *
 * The banner's "already have them" rail (VALE-890): the alternate ways in
 * (`secondaryAction` / `secondaryButtonAction`) do NOT compete with the primary
 * CTA. The CTA row carries exactly two things — the "Select files" affordance
 * and the drag hint — and the alternates sit below a hairline divider as quiet,
 * equally-weighted controls. Before this the banner offered four entry points
 * joined by three "or"s with nothing reading as primary, and dropping the PMS
 * button (no PMS connected) left the text link orphaned under a row that ended
 * mid-sentence.
 */

import { useEffect, useState } from 'react';
import type { DragEventHandler, KeyboardEvent, ReactNode } from 'react';
import Image from 'next/image';
import { Upload, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { surfaceVariants, iconTileVariants } from './file-drop-zone.variants';
import type { FileDropZoneVariant, SellConfig, SellIcon } from './types';

interface DragRootProps {
  onDragEnter: DragEventHandler<HTMLElement>;
  onDragLeave: DragEventHandler<HTMLElement>;
  onDragOver: DragEventHandler<HTMLElement>;
  onDrop: DragEventHandler<HTMLElement>;
}

interface FileDropZoneSurfaceProps {
  variant: FileDropZoneVariant;
  sell: SellConfig | null;
  isDragging: boolean;
  disabled?: boolean;
  /**
   * Whether the surface accepts drops. `false` → a non-droppable CTA: solid
   * border and no "drag and drop here" copy (activation is the caller's
   * `onBrowse`, e.g. opening a dialog).
   */
  droppable?: boolean;
  /** Neutral (sell-off) headline; ignored when `sell` is on. */
  title?: string;
  hintText?: string;
  /** How many files the caller already has listed (drives banner contraction). */
  fileCount?: number;
  onBrowse: () => void;
  /** An "already have them" route on the `banner` variant's rail (optional leading icon). */
  secondaryAction?: {
    label: string;
    onClick: () => void;
    icon?: LucideIcon;
    logoUrl?: string;
  };
  /** A brand-carrying "already have them" route on the same rail (optional `logoUrl`). */
  secondaryButtonAction?: {
    label: string;
    onClick: () => void;
    logoUrl?: string;
  };
  rootProps: DragRootProps;
}

/** The neutral `Upload` glyph, sized against the muted tile it sits on. */
const ICON_TILE_SIZE: Record<FileDropZoneVariant, string> = {
  banner: 'size-7',
  compact: 'size-5.5',
  mini: 'size-5'
};

/**
 * The sell glyph is sized separately, and larger (VALE-1251).
 *
 * The neutral sizes above were chosen against a tile that HAS a fill: `Upload`
 * reads as an icon on something, so filling about two thirds of the tile is
 * right. The sell tile lost its purple gradient in VALE-1251, so its glyph is
 * not sitting on anything any more, it IS the element, and at the neutral sizes
 * it floated inside an invisible box.
 *
 * Read these as DRAWN size, not declared: `ValeAIMarkIcon` scales 1.30x in
 * place, so the class below is the slot and the glyph deliberately exceeds it.
 * `--spacing` is 0.2125rem here, not the stock 0.25rem (the LF-5142 density
 * knob), so a `size-N` is N × 3.4px:
 *
 *   banner   tile size-15 (51px)   ← slot size-9 (30.6)  ← drawn 39.8  (78%)
 *   compact  tile size-11 (37.4px) ← slot size-7 (23.8)  ← drawn 30.9  (83%)
 *   mini     tile size-9  (30.6px) ← slot size-6 (20.4)  ← drawn 26.5  (87%)
 *
 * **Whole multiples only.** A fractional `size-6.5` does NOT generate in this
 * project, and Tailwind fails it silently: the class is emitted into the markup
 * and simply matches no rule, so the glyph falls back to its intrinsic size
 * with nothing to show that a size was ever asked for. Verified against
 * `app/globals.css` with the real `@theme` loaded before these values were
 * picked. Keep them in step with `iconTileVariants`' `size` if that moves.
 */
const SELL_ICON_TILE_SIZE: Record<FileDropZoneVariant, string> = {
  banner: 'size-9',
  compact: 'size-7',
  mini: 'size-6'
};

/** Inner layout — kept off the surface box so the drag overlay can fill it. */
const CONTENT_LAYOUT: Record<FileDropZoneVariant, string> = {
  banner: 'flex flex-col items-center text-center',
  compact: 'flex items-center gap-4',
  mini: 'flex items-center gap-3'
};

const CTA_PRIMARY =
  'inline-flex items-center gap-2 rounded-sm bg-primary px-3.5 py-2 text-sm font-medium text-primary-foreground';
const CTA_PRIMARY_SM =
  'inline-flex items-center gap-2 rounded-sm bg-primary px-3 py-1.5 text-13px font-medium text-primary-foreground';
const CTA_OUTLINE =
  'inline-flex items-center gap-2 rounded-sm border border-input bg-background px-3 py-1.5 text-sm font-medium text-foreground';

const NEUTRAL_TITLE = 'Drag & drop or click to upload';

/**
 * What a drop surface says while a file is over it.
 *
 * Exported because the drag-active moment is no longer only this box's (VALE-1592): a
 * dialog can make its whole body the drop target and render this copy across it, and two
 * places wording the same instant differently is what this constant exists to prevent.
 */
export const FILE_DROP_ZONE_DRAG_COPY = {
  title: 'Drop to upload',
  subtext: 'Release to add your file'
} as const;

const DRAG_TITLE = FILE_DROP_ZONE_DRAG_COPY.title;
const DRAG_SUBTEXT = FILE_DROP_ZONE_DRAG_COPY.subtext;

/**
 * The banner's drag hint — the ONLY "or" left in the empty state (VALE-890).
 * It reads plural to match the "Select files" CTA beside it, which is already
 * plural on every banner including the single-file ones.
 */
const BANNER_DRAG_HINT = 'or drag and drop them here';
/** Label opening the rail of demoted "I already have the documents" routes. */
const ALTERNATE_ROUTES_LABEL = 'Already have them?';

// The banner's height collapse (in either direction) animates cleanest when it
// starts AFTER the add/remove that triggered it has re-rendered the file list
// and repainted. Adding files mounts rows + kicks off progress; removing them
// unmounts rows + reflows the list — both are a burst of layout in the same
// frame, and starting the collapse then makes it stutter/jump. So hold the
// state flip one beat past that redraw, whichever way it's going.
const SETTLE_DELAY_MS = 60;

export function FileDropZoneSurface({
  variant,
  sell,
  isDragging,
  disabled,
  droppable = true,
  title,
  hintText,
  fileCount = 0,
  onBrowse,
  secondaryAction,
  secondaryButtonAction,
  rootProps
}: FileDropZoneSurfaceProps) {
  const state = disabled ? 'disabled' : isDragging ? 'dragging' : 'idle';
  const hasFiles = fileCount > 0;

  // Deferred contraction flag (banner only). Follows `hasFiles`, but the
  // contract direction is held back one beat so the collapse animates AFTER the
  // drop's file-row insertion + progress redraw, not during it. Expanding back
  // is immediate (no competing burst). On mount it matches `hasFiles`, so a
  // surface that already has files renders contracted with no animation.
  const [contracted, setContracted] = useState(hasFiles);
  useEffect(() => {
    if (contracted === hasFiles) return;
    // Defer the flip one beat in BOTH directions, so the collapse (contracting)
    // and the re-expand animate after the file-list add/remove redraw settles,
    // not during it.
    const timer = setTimeout(() => setContracted(hasFiles), SETTLE_DELAY_MS);
    return () => clearTimeout(timer);
  }, [hasFiles, contracted]);

  const handleClick = () => {
    if (!disabled) onBrowse();
  };
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onBrowse();
    }
  };

  const SellIcon = sell?.icon ?? Upload;
  /**
   * Whether a row variant renders the alternate-routes rail beneath its row
   * (VALE-895). `banner` is excluded because it renders its own inside
   * `BannerBody`, below the hint line; `mini` because {@link AlternateRoutesRail}
   * does not serve it. Gating the column wrapper on this keeps the row variants'
   * markup byte-identical when no route is passed, which is every existing
   * caller.
   */
  const showRowRail =
    variant === 'compact' && (!!secondaryAction || !!secondaryButtonAction);

  return (
    <div
      {...rootProps}
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      aria-label={sell ? sell.headline : (title ?? NEUTRAL_TITLE)}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={surfaceVariants({ variant, state, droppable })}
    >
      {/* Decorative "extraction" motif — violet (+ blue) blur blobs, only on
          the sell surfaces. Sit behind content (isolate + -z-10). The
          bottom-left blob drops out once the banner has contracted. */}
      {!!sell && variant === 'banner' && (
        <>
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -top-[90px] -right-[70px] -z-10 size-[200px] rounded-full bg-[radial-gradient(circle,rgba(124,58,237,0.22),transparent_70%)] blur-[46px]"
          />
          {!contracted && (
            <span
              aria-hidden="true"
              className="pointer-events-none absolute -bottom-[90px] -left-[70px] -z-10 size-[190px] rounded-full bg-[radial-gradient(circle,rgba(59,130,246,0.16),transparent_70%)] blur-[46px]"
            />
          )}
        </>
      )}
      {!!sell && variant === 'compact' && (
        <span
          aria-hidden="true"
          className="pointer-events-none absolute -top-[70px] -right-[50px] -z-10 size-[150px] rounded-full bg-[radial-gradient(circle,rgba(124,58,237,0.22),transparent_70%)] blur-[46px]"
        />
      )}

      {/* Real content — stays mounted during drag (invisible) so the drop
          target never resizes.

          The row variants lay out horizontally ([tile] [text] [Browse]), so a
          rail cannot live inside that row — it wraps the row in a column and
          sits underneath (VALE-895). The banner is already a column and owns
          its rail inside `BannerBody`, below its own hint line. */}
      <div
        className={cn(
          showRowRail && 'flex w-full flex-col',
          isDragging && 'invisible'
        )}
      >
        <div className={CONTENT_LAYOUT[variant]}>
          {/* The banner owns its own (collapsible) tile inside BannerBody; the
            row variants render their tile here. */}
          {variant !== 'banner' && (
            <span
              className={cn(
                iconTileVariants({ variant, size: variant, sell: !!sell })
              )}
            >
              {sell ? (
                <SellIcon
                  className={SELL_ICON_TILE_SIZE[variant]}
                  aria-hidden="true"
                />
              ) : (
                <Upload
                  className={ICON_TILE_SIZE[variant]}
                  aria-hidden="true"
                />
              )}
            </span>
          )}

          {variant === 'banner' ? (
            <BannerBody
              sell={sell}
              sellIcon={SellIcon}
              title={title}
              hintText={hintText}
              contracted={contracted}
              fileCount={fileCount}
              droppable={droppable}
              secondaryAction={secondaryAction}
              secondaryButtonAction={secondaryButtonAction}
            />
          ) : (
            <RowBody
              variant={variant}
              sell={sell}
              title={title}
              hintText={hintText}
              hasFiles={hasFiles}
              droppable={droppable}
            />
          )}
        </div>
        {showRowRail && (
          <AlternateRoutesRail
            variant={variant}
            secondaryAction={secondaryAction}
            secondaryButtonAction={secondaryButtonAction}
          />
        )}
      </div>

      {/* Drag feedback overlay — fills the (unchanged) box; violet title per
          the design. pointer-events-none so drag events reach the surface. */}
      {isDragging && (
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-1 px-4 text-center">
          <p
            className={cn(
              'text-ai-subtle-foreground font-semibold',
              variant === 'banner' ? 'text-lg' : 'text-sm'
            )}
          >
            {DRAG_TITLE}
          </p>
          <p
            className={cn(
              'text-muted-foreground',
              variant === 'banner' ? 'text-sm' : 'text-xs'
            )}
          >
            {DRAG_SUBTEXT}
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Height collapse via the grid-rows 0fr↔1fr technique — animates smoothly with
 * no text distortion and no JS measurement, and honours reduced-motion.
 */
function BannerCollapse({
  open,
  children
}: {
  open: boolean;
  children: ReactNode;
}) {
  return (
    <div
      aria-hidden={!open}
      className={cn(
        'grid w-full transition-[grid-template-rows] duration-300 ease-out motion-reduce:transition-none',
        open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      )}
    >
      <div className="flex min-h-0 flex-col items-center overflow-hidden">
        {children}
      </div>
    </div>
  );
}

function BannerBody({
  sell,
  sellIcon: SellIcon,
  title,
  hintText,
  contracted,
  fileCount,
  droppable,
  secondaryAction,
  secondaryButtonAction
}: {
  sell: SellConfig | null;
  sellIcon: SellIcon;
  title?: string;
  hintText?: string;
  contracted: boolean;
  fileCount: number;
  droppable: boolean;
  secondaryAction?: {
    label: string;
    onClick: () => void;
    icon?: LucideIcon;
    logoUrl?: string;
  };
  secondaryButtonAction?: {
    label: string;
    onClick: () => void;
    logoUrl?: string;
  };
}) {
  const headline = contracted
    ? sell
      ? 'Add more, or let Vale get started'
      : 'Add more files'
    : (sell?.headline ?? title ?? NEUTRAL_TITLE);

  return (
    <>
      {/* Hero tile — collapses away entirely once files are present. */}
      <BannerCollapse open={!contracted}>
        <span
          className={cn(
            iconTileVariants({
              variant: 'banner',
              size: 'banner',
              sell: !!sell
            }),
            'mb-4'
          )}
        >
          <SellIcon className={SELL_ICON_TILE_SIZE.banner} aria-hidden="true" />
        </span>
      </BannerCollapse>

      <h3
        className={cn(
          'text-foreground font-semibold tracking-tight text-balance',
          contracted ? 'text-17px' : 'text-lg'
        )}
      >
        {headline}
      </h3>

      {/* AI pitch — description + benefit chips. This is the "feature content"
          that switches out once files are added (collapses away). */}
      {!!sell && (sell.description || !!sell.benefits?.length) && (
        <BannerCollapse open={!contracted}>
          {!!sell.description && (
            <p className="text-muted-foreground mt-2 max-w-md text-sm">
              {sell.description}
            </p>
          )}
          {!!sell.benefits?.length && (
            <div className="mt-4 flex flex-wrap justify-center gap-2">
              {sell.benefits.map((b) => (
                <span
                  key={b.label}
                  className="border-ai/15 text-ai-subtle-foreground bg-ai/[0.07] inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium"
                >
                  <b.icon
                    className="text-ai-subtle-foreground size-3.5"
                    aria-hidden="true"
                  />
                  {b.label}
                </span>
              ))}
            </div>
          )}
        </BannerCollapse>
      )}

      <div
        className={cn(
          'flex flex-wrap items-center justify-center gap-3',
          contracted ? 'mt-3.5' : 'mt-5'
        )}
      >
        <span className={contracted ? CTA_PRIMARY_SM : CTA_PRIMARY}>
          <Upload
            className={contracted ? 'size-3.5' : 'size-4'}
            aria-hidden="true"
          />
          {contracted ? 'Add files' : 'Select files'}
        </span>
        {/* The "drag and drop" hint only belongs on a real drop target. This
            row carries NOTHING else — the alternate routes live on the rail
            below the divider, so the primary CTA has no rival (VALE-890). */}
        {droppable && (
          <span className="text-muted-foreground text-sm">
            {contracted
              ? `${fileCount} ${fileCount === 1 ? 'file' : 'files'} ready`
              : BANNER_DRAG_HINT}
          </span>
        )}
      </div>

      {/* Accepted file types — kept in BOTH states (divergence from the mock,
          which drops it once files are added). */}
      {!!hintText && (
        <p className="text-muted-foreground/70 mt-3 text-xs">{hintText}</p>
      )}

      {/* The "already have them" rail — see {@link AlternateRoutesRail}. It
          renders in both the expanded and contracted states; the PMS route
          drops out on its own when the caller stops passing it, and the label
          plus remaining control still read as a sentence with nothing
          orphaned. */}
      <AlternateRoutesRail
        variant="banner"
        secondaryAction={secondaryAction}
        secondaryButtonAction={secondaryButtonAction}
      />
    </>
  );
}

/**
 * The **"already have them" rail** — the routes for someone who does not need to
 * upload anything, because the document is already in Vale or in the firm's PMS
 * (VALE-890).
 *
 * Rendered below a full-width hairline, under its own label, as quiet **ghost**
 * controls of equal weight. Neither may become filled or outlined: the surface
 * has exactly one primary action ("Select files"/"Browse"), and an outline
 * button at near-equal weight beside it is precisely what stopped anything
 * reading as primary before VALE-890.
 *
 * **Available on `banner` and `compact`; deliberately not on `mini`** (VALE-895).
 * It began banner-only, which left every compact surface with nowhere to put a
 * PMS import route — and the props were accepted and silently dropped there,
 * so a caller passing one got no route, no type error and no warning. `mini` is
 * the small in-dialog utility drop; a separator, a label and two controls under
 * it would outweigh the surface itself, so `mini` still ignores these props and
 * this comment is where that is written down.
 *
 * The two variants differ only in alignment, following the body above them: the
 * banner is centred, the compact row is left-aligned. Making the compact rail
 * centred under a left-aligned row reads as a different component rather than a
 * continuation of the same one.
 */
function AlternateRoutesRail({
  variant,
  secondaryAction,
  secondaryButtonAction
}: {
  variant: FileDropZoneVariant;
  secondaryAction?: {
    label: string;
    onClick: () => void;
    icon?: LucideIcon;
    logoUrl?: string;
  };
  secondaryButtonAction?: {
    label: string;
    onClick: () => void;
    logoUrl?: string;
  };
}) {
  if (variant === 'mini') return null;
  if (!secondaryAction && !secondaryButtonAction) return null;

  const banner = variant === 'banner';

  return (
    <div className={cn('w-full', banner ? 'mt-4' : 'mt-3')}>
      <Separator className="bg-border/70" />
      <div
        className={cn(
          'flex flex-wrap items-center gap-x-1 gap-y-1',
          banner ? 'mt-3 justify-center' : 'mt-2 justify-start'
        )}
      >
        <span
          className={cn(
            'text-muted-foreground mr-1',
            banner ? 'text-sm' : 'text-xs'
          )}
        >
          {ALTERNATE_ROUTES_LABEL}
        </span>
        {!!secondaryButtonAction && (
          <AlternateRouteButton
            label={secondaryButtonAction.label}
            onClick={secondaryButtonAction.onClick}
            logoUrl={secondaryButtonAction.logoUrl}
          />
        )}
        {!!secondaryAction && (
          <AlternateRouteButton
            label={secondaryAction.label}
            onClick={secondaryAction.onClick}
            icon={secondaryAction.icon}
            logoUrl={secondaryAction.logoUrl}
          />
        )}
      </div>
    </div>
  );
}

/**
 * One control on the banner's "already have them" rail — a ghost button, so it
 * carries no fill and no border and cannot read as a rival to the primary CTA.
 *
 * The surface root is `role="button"` with its own onClick/onKeyDown wired to
 * `onBrowse`, so activating a rail control must NOT also open the native file
 * picker: stop propagation on both handlers. That subtlety is invisible to a
 * screenshot and is asserted directly (VALE-588, carried forward here).
 */
function AlternateRouteButton({
  label,
  onClick,
  logoUrl,
  icon: Icon
}: {
  label: string;
  onClick: () => void;
  logoUrl?: string;
  icon?: LucideIcon;
}) {
  return (
    <Button
      tone="strong"
      gap="standard"
      type="button"
      variant="ghost"
      size="xs"
      onClick={(e) => {
        e.stopPropagation();
        onClick();
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
      }}
    >
      {!!logoUrl && <Image src={logoUrl} alt="" width={14} height={14} />}
      {!logoUrl && !!Icon && (
        <Icon className="text-muted-foreground size-3.5" aria-hidden="true" />
      )}
      {label}
    </Button>
  );
}

function RowBody({
  variant,
  sell,
  title,
  hintText,
  hasFiles,
  droppable
}: {
  variant: FileDropZoneVariant;
  sell: SellConfig | null;
  title?: string;
  hintText?: string;
  hasFiles?: boolean;
  droppable: boolean;
}) {
  const heading = sell?.headline ?? title ?? NEUTRAL_TITLE;
  // Pitch/prompt line, plus — for sell surfaces — the file-types/size hint on
  // its OWN line beneath it. Neutral surfaces use the hint as the single line.
  let subtitle: string | undefined;
  let typesLine: string | undefined;
  if (hasFiles) {
    subtitle = droppable
      ? 'Drag more here, or browse your device'
      : 'Browse your device to add more';
  } else if (sell) {
    subtitle = sell.description;
    typesLine = hintText;
  } else {
    subtitle = hintText ?? 'or click to browse';
  }

  // Files present → quiet "Browse"; neutral → "Browse"; fresh sell → "Select files".
  const primary = !!sell && !hasFiles;

  return (
    <>
      <div className="min-w-0 flex-1 text-left">
        <p
          className={cn(
            'text-foreground truncate',
            variant === 'mini' ? 'text-sm font-medium' : 'text-sm font-semibold'
          )}
        >
          {heading}
        </p>
        {!!subtitle && (
          <p
            className={cn(
              'text-muted-foreground text-xs',
              // Compact: 4px below the title — the design doesn't sit the
              // subtext tight against the card title. Mini stays tight.
              variant === 'compact' && 'mt-1'
            )}
          >
            {subtitle}
          </p>
        )}
        {!!typesLine && (
          <p className="text-muted-foreground/70 text-12px mt-0.5">
            {typesLine}
          </p>
        )}
      </div>
      <span className={cn('shrink-0', primary ? CTA_PRIMARY : CTA_OUTLINE)}>
        {primary && <Upload className="size-4" aria-hidden="true" />}
        {primary ? 'Select files' : 'Browse'}
      </span>
    </>
  );
}
